/**
 * Cookie consent banner + preferences dialog
 * ==========================================
 *
 * Drives templates/components/layout/cookie_consent.html.
 *
 * This file owns the *UI* only. The stored choice, and everything that acts on it,
 * lives in the small inline snippet at the top of templates/base.html — it has to
 * run before any third-party tag, which a `{% static %}` script cannot guarantee.
 * The contract between the two is `window.CookieConsent`:
 *
 *   CookieConsent.answered()            -> has the visitor chosen yet?
 *   CookieConsent.get()                 -> {analytics, marketing} or null
 *   CookieConsent.save({analytics, marketing})
 *
 * Saving is the only write path. It persists the cookie, loads any tag that just
 * became allowed, and stops any tag that no longer is — so nothing here needs to
 * know GA4 or PostHog exist.
 *
 * Compliance notes, in case they look like bugs later:
 *   - Non-essential toggles start OFF. A pre-ticked box is not consent.
 *   - "Reject" and "Accept" are styled identically; nudging with a loud Accept and
 *     a faint Reject is the dark pattern regulators name explicitly.
 *   - Dismissing the dialog is not a choice — the banner stays up until a button
 *     is pressed, and the tags stay off in the meantime.
 *   - The footer's "Cookie preferences" link calls window.showCookiePreferences()
 *     so a visitor can change their mind at any time.
 */

(function () {
    'use strict';

    var SHOW_DELAY_MS = 800;   // let the page paint before the banner slides in

    var banner = document.getElementById('cookie-consent');
    var modal = document.getElementById('cookie-preferences-modal');
    if (!banner || !modal) return;

    var modalPanel = modal.querySelector('[data-cc-panel]');
    var analyticsToggle = document.getElementById('cc-analytics');
    var marketingToggle = document.getElementById('cc-marketing');
    var lastFocused = null;

    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    /* ---------------------------------------------------------------- banner */

    /* The banner is `fixed bottom-4 left-4 right-4` and full width below sm, so on a
     * phone it lands exactly on top of any bottom-sticky action bar — the booking
     * form's Continue / Submit button among them, which then swallowed every click
     * and read as a dead button until the visitor dealt with the banner.
     *
     * Rather than fight it with z-index (raising the bar would bury the consent
     * choice instead, which is worse), the banner publishes how much room it is
     * taking at the bottom of the viewport. A sticky bar sits above it with
     * `bottom-[var(--cookie-banner-h,0px)]` and drops back to the edge the moment
     * the banner goes.
     */
    var BANNER_GAP_PX = 16; // matches `bottom-4`

    function publishBannerHeight() {
        var height = 0;
        if (!banner.hidden) {
            height = banner.getBoundingClientRect().height + BANNER_GAP_PX * 2;
        }
        document.documentElement.style.setProperty('--cookie-banner-h', height + 'px');
    }

    function showBanner() {
        banner.hidden = false;
        // Next frame, so the browser has a start position to transition from.
        requestAnimationFrame(function () {
            banner.classList.remove('translate-y-[130%]', 'opacity-0');
            publishBannerHeight();
        });
    }

    function hideBanner() {
        banner.classList.add('translate-y-[130%]', 'opacity-0');
        // Cleared as the banner starts leaving, not when it finishes: the bar
        // dropping back a beat early is invisible, a button that is still covered
        // for 300ms is not.
        document.documentElement.style.setProperty('--cookie-banner-h', '0px');
        window.setTimeout(function () { banner.hidden = true; }, 300);
    }

    /* ---------------------------------------------------------------- dialog */

    function openModal() {
        lastFocused = document.activeElement;
        syncToggles();
        modal.hidden = false;
        document.body.classList.add('overflow-hidden');
        var first = modalPanel.querySelector(FOCUSABLE);
        if (first) first.focus();
        if (window.lucide && window.lucide.createIcons) window.lucide.createIcons();
    }

    function closeModal() {
        modal.hidden = true;
        document.body.classList.remove('overflow-hidden');
        // Send focus back where it came from, or to the banner if that is still up.
        if (lastFocused && document.contains(lastFocused)) {
            lastFocused.focus();
        } else if (!banner.hidden) {
            banner.focus();
        }
        lastFocused = null;
    }

    function isModalOpen() { return !modal.hidden; }

    // Keep Tab inside the dialog while it is open.
    function trapFocus(event) {
        if (event.key !== 'Tab') return;
        var items = Array.prototype.filter.call(
            modalPanel.querySelectorAll(FOCUSABLE),
            function (el) { return el.offsetParent !== null; }
        );
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }

    /* ------------------------------------------------------------- decisions */

    function syncToggles() {
        var prefs = window.CookieConsent.get();
        // No prior answer -> both off. Never pre-tick a non-essential category.
        if (analyticsToggle) analyticsToggle.checked = !!(prefs && prefs.analytics);
        if (marketingToggle) marketingToggle.checked = !!(prefs && prefs.marketing);
    }

    function decide(prefs) {
        window.CookieConsent.save(prefs);
        if (isModalOpen()) closeModal();
        hideBanner();
    }

    var actions = {
        accept: function () { decide({ analytics: true, marketing: true }); },
        reject: function () { decide({ analytics: false, marketing: false }); },
        save: function () {
            decide({
                analytics: !!(analyticsToggle && analyticsToggle.checked),
                marketing: !!(marketingToggle && marketingToggle.checked)
            });
        },
        customize: openModal,
        close: closeModal
    };

    document.addEventListener('click', function (event) {
        var trigger = event.target.closest('[data-cc-action]');
        if (trigger) {
            var action = actions[trigger.getAttribute('data-cc-action')];
            if (action) {
                event.preventDefault();
                action();
            }
            return;
        }
        // Click on the backdrop (outside the panel) closes without deciding.
        if (isModalOpen() && !event.target.closest('[data-cc-panel]')) closeModal();
    });

    document.addEventListener('keydown', function (event) {
        if (!isModalOpen()) return;
        if (event.key === 'Escape') { closeModal(); return; }
        trapFocus(event);
    });

    /* ------------------------------------------------------------------ boot */

    // Re-opening the preferences later: used by the footer link, and safe to call
    // from anywhere (a page CTA, a policy page button).
    window.addEventListener('resize', function () {
        if (!banner.hidden) publishBannerHeight();
    });

    window.showCookiePreferences = openModal;

    if (!window.CookieConsent.answered()) {
        window.setTimeout(showBanner, SHOW_DELAY_MS);
    }
})();
